import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { Subject, interval, of, Observable, combineLatest, Subscription } from 'rxjs';
import { filter, takeUntil, catchError, map, mergeMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HelperService } from './services/helper.service';
import { PermissionsService } from './services/permissions.service';
import { NotificationService } from './services/notification.service';
import { ApiDenialService } from './services/api-denial.service';
import { UserSession } from './models/permissions.model';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'carehub';
  isIframe = false;
  loginDisplay = false;
  isUAT = false;
  envBgClass!: string;
  envTextClass!: string;
  userSession: UserSession | null = null;
  hasSchedulerAccess$: Observable<boolean>;
  schedulerButtonText$: Observable<string>;
  private readonly _destroying$ = new Subject<void>();
  private readonly TOKEN_REFRESH_CHECK_INTERVAL_MS = 5 * 60 * 1000;
  private readonly TOKEN_REFRESH_WINDOW_SECONDS = 5 * 60;
  private tokenExpirationTimer!: Subscription;
  private lastRoute = '/';
  isRosterPage = false;
  isWorklogPage = false;
  isAdhocServicePage = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    public permissionsService: PermissionsService,
    private helperService: HelperService,
    private router: Router,
    private notificationService: NotificationService,
    private apiDenial: ApiDenialService
  ) {
    this.hasSchedulerAccess$ = this.permissionsService.hasAnyFeatureAccess(['roster', 'reallocate']);
    this.schedulerButtonText$ = this.permissionsService.getHighestFeatureAccess().pipe(
      map(highestFeature => {
        if (highestFeature === 'roster') return 'Schedule';
        if (highestFeature === 'reallocate') return 'Reallocate';
        return '';
      })
    );

    // Track the last route for environment switching and page detection
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      this.lastRoute = event.url;
      this.isRosterPage = event.url.includes('/roster');
      this.isWorklogPage = event.url.includes('/worklog-editor');
      this.isAdhocServicePage = event.url.includes('/adhoc-service');
      // Store the current route in localStorage for refresh handling
      localStorage.setItem('last_route', event.url);
    });
  }

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;
    this.setLoginDisplay();

    const savedEnv = this.helperService.getStoredValue('ecase_env', 'prod');
    this.isUAT = savedEnv === 'uat';
    this.envBgClass = this.isUAT ? 'uat-bg' : 'prod-bg';
    this.envTextClass = this.isUAT ? 'uat-text' : 'prod-text';

    // Wait for MSAL initialization before initializing user session
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1),
        mergeMap(() => this.permissionsService.initializeUserSession()),
        takeUntil(this._destroying$)
      ).subscribe({
        next: () => {
          // Session initialized successfully
          this.restoreLastRoute();
        },
        error: (error) => {
        }
      });

    this.authService.instance.enableAccountStorageEvents();
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      )
      .subscribe((msg: EventMessage) => {
        if (msg.eventType === EventType.ACCOUNT_REMOVED) {
          this.permissionsService.clearCachedSession();
        }
        if (this.authService.instance.getAllAccounts().length === 0) {
          this.router.navigate(['/']);
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    this.permissionsService.userSession$.subscribe(
      session => this.userSession = session
    );

    // API 403 denials (tier, prod writes off, feature off, not in eCase) go to the notification panel.
    this.apiDenial.denials$
      .pipe(takeUntil(this._destroying$))
      .subscribe(d => this.notificationService.addMessage('critical', d.detail, undefined, 'api-denial'));

    this.startTokenExpirationCheck();
  }

  private restoreLastRoute() {
    const lastRoute = localStorage.getItem('last_route');
    // Remove any hash from the current path for comparison
    const currentPath = window.location.pathname.replace(/^\//, '');
    if (lastRoute && lastRoute !== '/' && lastRoute !== currentPath) {
      // Remove leading slash if present for consistent navigation
      const cleanRoute = lastRoute.replace(/^\//, '');
      this.router.navigate([cleanRoute]);
    }
  }

  toggleEnvironment() {
    this.isUAT = !this.isUAT;
    this.envBgClass = this.isUAT ? 'uat-bg' : 'prod-bg';
    this.envTextClass = this.isUAT ? 'uat-text' : 'prod-text';
    const newEnv = this.isUAT ? 'uat' : 'prod';
    this.helperService.storeValue('ecase_env', newEnv);
    
    // Store the environment change timestamp
    localStorage.setItem('env_change_timestamp', Date.now().toString());
    
    // Preserve the current route and reload
    localStorage.setItem('last_route', this.lastRoute);
    window.location.reload();
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
          this.storeTokenExpiry(response);
        });
    } else {
      this.authService.loginPopup()
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
          this.storeTokenExpiry(response);
        });
    }
  }

  logout(popup?: boolean) {
    this.permissionsService.clearCachedSession();
    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: "/"
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  storeTokenExpiry(response: AuthenticationResult) {
    const expiresOn = response.expiresOn;
    if (expiresOn) {
      localStorage.setItem('token_expiry', expiresOn.toISOString());
    }
  }

  private startTokenExpirationCheck() {
    this.tokenExpirationTimer = interval(this.TOKEN_REFRESH_CHECK_INTERVAL_MS).subscribe(() => {
      const account = this.authService.instance.getActiveAccount();
      if (account && this.shouldRefreshToken(account)) {
        this.authService.acquireTokenSilent({
          scopes: environment.graphApi.scopes,
          account: account,
          forceRefresh: true
        }).pipe(
          catchError((error: Error) => {
            // Don't trigger redirect from a background timer.
            // Let user-initiated flows/routes handle interactive auth.
            if (error instanceof InteractionRequiredAuthError) return of(null);
            return of(null);
          })
        ).subscribe();
      }
    });
  }

  private shouldRefreshToken(account: any): boolean {
    const exp = account?.idTokenClaims?.exp;
    if (typeof exp !== 'number') {
      return false;
    }
    const nowSeconds = Math.floor(Date.now() / 1000);
    return exp - nowSeconds <= this.TOKEN_REFRESH_WINDOW_SECONDS;
  }

  reloadPage(event: Event): void {
    event.preventDefault();
    // Store current route before reload
    localStorage.setItem('last_route', this.router.url);
    window.location.reload();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    if (this.tokenExpirationTimer) {
      this.tokenExpirationTimer.unsubscribe();
    }
  }
}
